import { Injectable } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { combineLatest, map, Observable, Subject, take } from 'rxjs';
import { QuickAccessCustomizerComponent } from 'src/app/shared/components/quick-access-customizer/quick-access-customizer.component';
import { PERMITTED_VIEWS } from '../../helpers/global/global.constant';
import { QUICK_ACCESS_CATALOG } from '../../helpers/ui/quick-access-catalog';
import {
  MAX_QUICK_ACCESS_ITEMS,
  QUICK_ACCESS_DEFAULT_PRIORITY_IDS,
  QUICK_ACCESS_FLOATING_POSITION_KEY_PREFIX,
  QUICK_ACCESS_FLOATING_SIDE_KEY_PREFIX,
  QUICK_ACCESS_HINT_SEEN_KEY_PREFIX,
  QUICK_ACCESS_ICONS_KEY_PREFIX,
  QUICK_ACCESS_LABELS_KEY_PREFIX,
  QUICK_ACCESS_MIDDLE_SLOT_INDEX,
  QUICK_ACCESS_NAV_MODE_BOTTOM_BAR,
  QUICK_ACCESS_NAV_MODE_FLOATING_BUTTON,
  QUICK_ACCESS_NAV_MODE_HIDDEN,
  QUICK_ACCESS_NAV_MODE_KEY_PREFIX,
  QUICK_ACCESS_SHEET_DIALOG_CLASS,
  QUICK_ACCESS_SHEET_WINDOW_CLASS,
  QUICK_ACCESS_STORAGE_KEY_PREFIX,
} from '../../helpers/ui/quick-access.constant';
import {
  QuickAccessCustomizerResult,
  QuickAccessFloatingSide,
  QuickAccessNavMode,
  QuickAccessPage,
} from '../../interfaces/ui/quick-access.interface';
import { RoleService } from '../api/role.service';
import { GlobalService } from './global.service';

@Injectable({ providedIn: 'root' })
export class QuickAccessService {
  readonly maxItems = MAX_QUICK_ACCESS_ITEMS;

  private selectionChanged = new Subject<void>();
  readonly selectionChanged$ = this.selectionChanged.asObservable();

  private customizerOpen: boolean = false;

  constructor(
    private globalService: GlobalService,
    private roleService: RoleService,
    private ngbModal: NgbModal
  ) {}

  public openCustomizer(): void {
    if (this.customizerOpen) return;
    this.customizerOpen = true;

    combineLatest([this.globalService.profile, this.getPermittedPages$()])
      .pipe(take(1))
      .subscribe(([profile, permittedPages]) => {
        const userId = profile?._id ?? '';
        if (!userId) {
          this.customizerOpen = false;
          return;
        }

        const modalRef = this.ngbModal.open(QuickAccessCustomizerComponent, {
          windowClass: QUICK_ACCESS_SHEET_WINDOW_CLASS,
          modalDialogClass: QUICK_ACCESS_SHEET_DIALOG_CLASS,
          centered: false,
          backdrop: true,
        });

        modalRef.componentInstance.catalog = permittedPages;
        modalRef.componentInstance.selectedIds = this.resolveSelection(userId, permittedPages).map(
          (slot) => slot?.id ?? null
        );
        modalRef.componentInstance.customLabels = this.getCustomLabels(userId);
        modalRef.componentInstance.customIcons = this.getCustomIcons(userId);
        modalRef.componentInstance.navMode = this.getNavMode(userId);
        modalRef.componentInstance.defaultIds = this.buildDefaultSlots(permittedPages).map(
          (slot) => slot?.id ?? null
        );

        modalRef.closed.subscribe((result: QuickAccessCustomizerResult) => {
          this.saveSelection(userId, result.ids);
          this.applyLabelChanges(userId, result.labelChanges);
          this.applyIconChanges(userId, result.iconChanges);
          this.saveNavMode(userId, result.navMode);
          this.selectionChanged.next();
          this.customizerOpen = false;
        });
        modalRef.dismissed.subscribe(() => {
          this.customizerOpen = false;
        });
      });
  }

  public getPermittedPages$(): Observable<QuickAccessPage[]> {
    return this.globalService.profile.pipe(
      map((profile) => {
        const permittedIds = profile?.roleId?.isActive
          ? this.roleService.getIdsWithTrueStatus(profile.roleId.permissions)
          : [];
        return QUICK_ACCESS_CATALOG.filter((page) => permittedIds.includes(page.id));
      })
    );
  }

  public resolveSelection(
    userId: string,
    permittedPages: QuickAccessPage[]
  ): (QuickAccessPage | null)[] {
    const saved = this.loadSelection(userId);
    const labels = this.getCustomLabels(userId);
    const icons = this.getCustomIcons(userId);
    const decorate = (page: QuickAccessPage | null): QuickAccessPage | null => {
      if (!page) return null;
      const customLabel = labels[page.id];
      const customIcon = icons[page.id];
      if (!customLabel && !customIcon) return page;
      return {
        ...page,
        ...(customLabel ? { customLabel } : {}),
        ...(customIcon ? { customIcon } : {}),
      };
    };

    if (!saved) return this.buildDefaultSlots(permittedPages).map(decorate);

    return Array.from({ length: MAX_QUICK_ACCESS_ITEMS }, (_, i) => {
      const id = saved[i];
      if (!id) return null;
      return decorate(permittedPages.find((page) => page.id === id) ?? null);
    });
  }

  private saveSelection(userId: string, ids: (string | null)[]): void {
    localStorage.setItem(this.storageKey(userId), JSON.stringify(ids));
  }

  private getCustomLabels(userId: string): Record<string, string> {
    return this.readOverrides(this.labelsKey(userId));
  }

  private getCustomIcons(userId: string): Record<string, string> {
    return this.readOverrides(this.iconsKey(userId));
  }

  private applyLabelChanges(userId: string, changes: Record<string, string | null>): void {
    this.writeOverrides(this.labelsKey(userId), changes);
  }

  private applyIconChanges(userId: string, changes: Record<string, string | null>): void {
    this.writeOverrides(this.iconsKey(userId), changes);
  }

  private readOverrides(key: string): Record<string, string> {
    const raw = localStorage.getItem(key);
    if (!raw) return {};

    try {
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      return {};
    }
  }

  private writeOverrides(key: string, changes: Record<string, string | null>): void {
    const overrides = this.readOverrides(key);

    for (const [pageId, value] of Object.entries(changes)) {
      if (value) overrides[pageId] = value;
      else delete overrides[pageId];
    }

    localStorage.setItem(key, JSON.stringify(overrides));
  }

  public getNavMode(userId: string): QuickAccessNavMode {
    const raw = localStorage.getItem(this.navModeKey(userId));

    if (raw === QUICK_ACCESS_NAV_MODE_FLOATING_BUTTON) {
      return QUICK_ACCESS_NAV_MODE_FLOATING_BUTTON;
    }
    if (raw === QUICK_ACCESS_NAV_MODE_HIDDEN) return QUICK_ACCESS_NAV_MODE_HIDDEN;

    return QUICK_ACCESS_NAV_MODE_BOTTOM_BAR;
  }

  private saveNavMode(userId: string, mode: QuickAccessNavMode): void {
    localStorage.setItem(this.navModeKey(userId), mode);
  }

  public getFloatingPositionY(userId: string): number | null {
    const raw = localStorage.getItem(this.floatingPositionKey(userId));
    if (!raw) return null;

    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
  }

  public saveFloatingPositionY(userId: string, y: number): void {
    localStorage.setItem(this.floatingPositionKey(userId), String(y));
  }

  public getFloatingSide(userId: string): QuickAccessFloatingSide {
    const raw = localStorage.getItem(this.floatingSideKey(userId));
    return raw === 'left' ? 'left' : 'right';
  }

  public saveFloatingSide(userId: string, side: QuickAccessFloatingSide): void {
    localStorage.setItem(this.floatingSideKey(userId), side);
  }

  public hasSeenCustomizerHint(userId: string): boolean {
    return localStorage.getItem(`${QUICK_ACCESS_HINT_SEEN_KEY_PREFIX}${userId}`) === 'true';
  }

  public markCustomizerHintSeen(userId: string): void {
    localStorage.setItem(`${QUICK_ACCESS_HINT_SEEN_KEY_PREFIX}${userId}`, 'true');
  }

  private buildDefaultSlots(permittedPages: QuickAccessPage[]): (QuickAccessPage | null)[] {
    const slots: (QuickAccessPage | null)[] = new Array(MAX_QUICK_ACCESS_ITEMS).fill(null);
    const used = new Set<string>();

    const transferPage = permittedPages.find(
      (page) => page.id === PERMITTED_VIEWS.BTN_TRANSFER_PLAYER
    );
    if (transferPage) {
      slots[QUICK_ACCESS_MIDDLE_SLOT_INDEX] = transferPage;
      used.add(transferPage.id);
    }

    const fillOrder = Array.from({ length: MAX_QUICK_ACCESS_ITEMS }, (_, i) => i)
      .filter((index) => index !== QUICK_ACCESS_MIDDLE_SLOT_INDEX)
      .concat(QUICK_ACCESS_MIDDLE_SLOT_INDEX);

    let cursor = 0;
    const nextEmptyIndex = (): number => {
      while (cursor < fillOrder.length && slots[fillOrder[cursor]] !== null) cursor++;
      return cursor < fillOrder.length ? fillOrder[cursor] : MAX_QUICK_ACCESS_ITEMS;
    };

    const fillFrom = (pages: QuickAccessPage[]): void => {
      for (const page of pages) {
        if (used.has(page.id)) continue;
        const index = nextEmptyIndex();
        if (index >= MAX_QUICK_ACCESS_ITEMS) return;
        slots[index] = page;
        used.add(page.id);
      }
    };

    const priorityPages = QUICK_ACCESS_DEFAULT_PRIORITY_IDS.map((id) =>
      permittedPages.find((page) => page.id === id)
    ).filter((page): page is QuickAccessPage => !!page);

    fillFrom(priorityPages);
    fillFrom(permittedPages);

    return slots;
  }

  private loadSelection(userId: string): (string | null)[] | null {
    const raw = localStorage.getItem(this.storageKey(userId));
    if (!raw) return null;

    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : null;
    } catch {
      return null;
    }
  }

  private storageKey(userId: string): string {
    return `${QUICK_ACCESS_STORAGE_KEY_PREFIX}${userId}`;
  }

  private labelsKey(userId: string): string {
    return `${QUICK_ACCESS_LABELS_KEY_PREFIX}${userId}`;
  }

  private iconsKey(userId: string): string {
    return `${QUICK_ACCESS_ICONS_KEY_PREFIX}${userId}`;
  }

  private navModeKey(userId: string): string {
    return `${QUICK_ACCESS_NAV_MODE_KEY_PREFIX}${userId}`;
  }

  private floatingPositionKey(userId: string): string {
    return `${QUICK_ACCESS_FLOATING_POSITION_KEY_PREFIX}${userId}`;
  }

  private floatingSideKey(userId: string): string {
    return `${QUICK_ACCESS_FLOATING_SIDE_KEY_PREFIX}${userId}`;
  }
}
